import {
    IAngularEvent,
    IComponentController,
    IComponentOptions,
    IScope,
    ITimeoutService,
} from 'angular';
import { StateService } from 'angular-ui-router';
import { CartService } from '../../../../tn.cart/service.tn.cart';
import { LocalFavorites } from '../../../../tn.favorites/local-favorites.service/localFavorites';
import { ISeries } from '../../../../tn.fonts/SeriesService';
import { IFontFamilyModel, IFontStyleModel } from '../../../classes';
import {
    FONT_WIDTH_OPTIONS,
    FontRecommededSize,
    FontWidth,
} from '../../../constants';
import { FontListParams, FontStylePublicService } from '../../../providers';
import { FilterServicePersisted } from '../../../providers/FilterService2';
import familySpecimenTemplate from './family-specimen.html';

export interface FontGroup<T extends FontWidth> {
    name: string;
    fonts: IFontStyleModel[];
    value: T;
}
const FILTER_UPDATE_BOUNCE_TIME = 500; // ms

class FamilySpecimenControllerBindings {
    family?: IFontFamilyModel;
    activeFilters?: FontListParams;
    scale?: number;

    customText?: string;
    customTextSize?: FontRecommededSize;

    canonicalSeries?: ISeries;

    onBuyFamilyClicked?: ({ $event, family }) => void;
    onBuyStyleClicked?: ({ $event, style }) => void;
}

type TopLevelClasses = {
    custom?: boolean;
    table?: boolean;
    xsmall?: boolean;
    small?: boolean;
    medium?: boolean;
    large?: boolean;
    xlarge?: boolean;
    hasItalic?: boolean;
};

type SortOptions = {
    weight: { asc: boolean };
    width: { asc: boolean };
};

type Features = {
    sizecats: {
        [sizeLabel: string]: boolean;
    };
    customtext?: string;
    italic?: boolean;
};

class FamilySpecimenController
    extends FamilySpecimenControllerBindings
    implements IComponentController
{
    variable_fonts: IFontStyleModel[] = [];

    defaultStyle?: IFontStyleModel;

    topLevelClasses: TopLevelClasses = {};
    filtered_styles: IFontStyleModel[] = [];
    sortOptions: SortOptions = {
        weight: { asc: true },
        width: { asc: true },
    };
    features: Features = {
        sizecats: {},
    }; //local things to keep track of
    filtersChangedTimeout;
    isOpen = false;
    filters = this.filterService.get();
    favoriteStyleIds = this.localFavorites.getFavoriteStyleIds();
    favoriteFamilyIds = this.localFavorites.getFavoriteFamilyIds();
    groups: Record<number, FontGroup<FontWidth>> = {};

    oncustomtext = (
        event: IAngularEvent | undefined,
        text: string,
        oldtext = undefined
    ): void => {
        if ((!!text && !oldtext) || (!text && oldtext)) {
            this.fetchStyles();
        }
    };

    // XXX: this will cause a layout after initialization as width and weight
    //      extents are populated, decreasing initial page responsiveness
    onFiltersChanged = (): void => {
        if (this.filtersChangedTimeout) {
            this.$timeout.cancel(this.filtersChangedTimeout);
        }

        //wait a second to actually update styles
        this.filtersChangedTimeout = this.$timeout(
            () => this.fetchStyles(),
            FILTER_UPDATE_BOUNCE_TIME,
            false
        );
    };

    static $inject = [
        '$scope',
        '$timeout',
        'tnFontFilterService',
        'localFavorites',
        'tn.cart.service',
        '$state',
        'FontStylePublicService',
    ];
    constructor(
        protected $scope: IScope,
        protected $timeout: ITimeoutService,
        protected filterService: FilterServicePersisted,
        protected localFavorites: LocalFavorites,
        protected cartService: CartService,
        protected $state: StateService,
        protected styleService: FontStylePublicService
    ) {
        super();
    }

    $onInit(): void {
        // console.log('FamilySpecimenController.$onInit', this);
        this.defaultStyle = this.family?.def_style;
        this.isOpen = false;

        this.$scope.$watch('vm.activeFilters', this.onFiltersChanged);
        //initial style load
        this.fetchStyles();
    }

    openStyles = ($event: IAngularEvent): void => {
        $event.preventDefault();
        this.isOpen = true;
    };

    closeStyles = ($event: IAngularEvent): void => {
        // console.log('fam closesTyles');
        $event.preventDefault();
        this.isOpen = false;
    };

    buyFamily = ($event: Event): void => {
        if (!this.onBuyFamilyClicked) return;
        this.onBuyFamilyClicked({
            $event,
            family: this.family,
        });
    };

    buyStyle = ($event: Event, style: IFontStyleModel): void => {
        if (!this.onBuyStyleClicked) return;
        this.onBuyStyleClicked({
            $event,
            style,
        });
    };

    fetchStyles(): void {
        if (!this.family) return;

        this.styleService
            .list({
                ...this.activeFilters,
                id: this.family.style,
                // we want all the styles
                page_size: 1000,
            })
            .then((response) => {
                // if family was unset between rendering and response, do nothing.
                if (!this.family) {
                    this.defaultStyle = undefined;
                    return;
                }
                // if the default style is filtered out, fallback to the first
                // visible style.
                this.defaultStyle =
                    response.results.find(
                        (s) => s.id == this.family?.default_style
                    ) || response.results[0];
                return this.updateSpecimens(response.results);
            });
    }

    updateSpecimens(styles: IFontStyleModel[]): void {
        this.filtered_styles = styles;

        // initialize groups for expanded view.
        const groups: Record<number, FontGroup<FontWidth>> = {};
        FONT_WIDTH_OPTIONS.forEach((option) => {
            const value = option.value;
            const name = option.label;
            groups[value] = {
                name,
                value,
                fonts: [],
            };
        });

        styles.forEach((style) => {
            const id = style.width;
            if (!groups[id]?.name) {
                console.warn(
                    'Please contact suppport@typenetwork.com and let us know there is a missing FontWidth definiton. Please include the following information:',
                    { style: style.label, id: style.id, width: style.width }
                );
                return;
            }
            groups[id].fonts.push(style);
        });
        this.groups = groups;
    }
}

export const FamilySpecimenComponentOptions: IComponentOptions = {
    bindings: {
        // Required Props
        family: '<', // IFontFamilyModel
        canonicalSeries: '<', // Canonical Series for the `family`
        activeFilters: '<', // used to filter family styles in component

        // this is a multiplier to scale the size by.
        scale: '<',

        // optional props
        customText: '<',

        // optional events
        onBuyFamilyClicked: '&',
        onBuyStyleClicked: '&',
    },
    template: familySpecimenTemplate,
    controller: FamilySpecimenController,
};
